import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';

import { cn, createTestIdHelper } from '../../../lib/utils';
import { Button } from '../button';
import { Calendar } from '../calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

const testId = createTestIdHelper('date-picker');

export interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  /** Disables the whole control (cannot open the calendar). */
  disabled?: boolean;
  /** Earliest selectable date (inclusive). */
  minDate?: Date;
  /** Latest selectable date (inclusive). */
  maxDate?: Date;
  /** date-fns format for the trigger label. Defaults to "MM / dd / yyyy". */
  dateFormat?: string;
  /** Renders the trigger with a destructive border for invalid state. */
  error?: boolean;
  id?: string;
  className?: string;
  align?: React.ComponentProps<typeof PopoverContent>['align'];
  'data-testid'?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'MM / DD / YYYY',
  disabled = false,
  minDate,
  maxDate,
  dateFormat = 'MM / dd / yyyy',
  error = false,
  id,
  className,
  align = 'start',
  'data-testid': dataTestId,
}: DatePickerProps) {
  // react-day-picker takes a list of matchers; translate min/max into them.
  const disabledDays = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];

  // Controlled so a single-date selection can close the calendar (see onSelect).
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={dataTestId ?? testId('trigger')}
          className={cn(
            // `border-input` to match standard form inputs; the outline button
            // variant's `border-primary` reads much darker next to them.
            'h-10 w-full justify-start border-input text-left font-normal',
            error && 'border-destructive',
            className,
          )}
        >
          <CalendarIcon className="mr-2 size-4 opacity-50" aria-hidden />
          <span className={cn('flex-1', !value && 'text-muted-foreground')}>
            {value ? format(value, dateFormat) : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[60] w-auto p-1"
        align={align}
        sideOffset={4}
        data-testid={testId('calendar')}
      >
        <Calendar
          mode="single"
          selected={value}
          onSelect={(date) => {
            onChange?.(date);
            // Single-date mode: a pick is the full selection, so dismiss.
            setOpen(false);
          }}
          initialFocus
          disabled={disabledDays.length > 0 ? disabledDays : undefined}
          className="text-sm"
        />
      </PopoverContent>
    </Popover>
  );
}
